import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateTicketReservationTagsByFilterOptions,
  BulkUpdateTicketReservationTagsByFilterResponse,
  BulkUpdateTicketReservationTagsOptions,
  BulkUpdateTicketReservationTagsResponse,
  CancelTicketReservationResponse,
  DeleteTicketReservationResponse,
  TicketReservation,
  TicketReservationCreatedEnvelope,
  TicketReservationDeletedEnvelope,
  TicketReservationUpdatedEnvelope,
  bulkUpdateTicketReservationTags as universalBulkUpdateTicketReservationTags,
  bulkUpdateTicketReservationTagsByFilter as universalBulkUpdateTicketReservationTagsByFilter,
  cancelTicketReservation as universalCancelTicketReservation,
  createTicketReservation as universalCreateTicketReservation,
  deleteTicketReservation as universalDeleteTicketReservation,
  getTicketReservation as universalGetTicketReservation,
} from './events-v1-ticket-reservation-ticket-reservations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function createTicketReservation(
  httpClient: HttpClient
): CreateTicketReservationSignature {
  return (
    ticketReservation: NonNullablePaths<
      TicketReservation,
      `tickets.${number}.quantity` | `tickets.${number}.ticketDefinitionId`,
      4
    >
  ) =>
    universalCreateTicketReservation(
      ticketReservation,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTicketReservationSignature {
  /**
   * Creates a ticket reservation.
   *
   * Reserves specified tickets for a limited time while a customer completes their purchase.
   * The reservation will have `PENDING` status and will automatically expire after the
   * event's configured reservation duration (typically 5-30 minutes) unless confirmed.
   * @param - Ticket reservation details to create.
   *
   * Must include at least one ticket line item with valid ticket definition ID
   * and quantity. Additional settings like seat assignments and pricing options
   * can be specified in the ticket info.
   * @returns The created ticket reservation.
   */
  (
    ticketReservation: NonNullablePaths<
      TicketReservation,
      `tickets.${number}.quantity` | `tickets.${number}.ticketDefinitionId`,
      4
    >
  ): Promise<
    NonNullablePaths<
      TicketReservation,
      | `status`
      | `tickets`
      | `tickets.${number}.ticketInfo.pricingPlan.orderId`
      | `tickets.${number}.ticketInfo.pricingPlan.planId`
      | `tickets.${number}.price.value`
      | `tickets.${number}.price.currency`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function getTicketReservation(
  httpClient: HttpClient
): GetTicketReservationSignature {
  return (ticketReservationId: string) =>
    universalGetTicketReservation(
      ticketReservationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTicketReservationSignature {
  /**
   * Retrieves a ticket reservation by ID.
   * @param - ID of the ticket reservation to retrieve.
   * @returns The requested ticket reservation.
   */
  (ticketReservationId: string): Promise<
    NonNullablePaths<
      TicketReservation,
      | `status`
      | `tickets`
      | `tickets.${number}.ticketInfo.pricingPlan.orderId`
      | `tickets.${number}.ticketInfo.pricingPlan.planId`
      | `tickets.${number}.price.value`
      | `tickets.${number}.price.currency`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function deleteTicketReservation(
  httpClient: HttpClient
): DeleteTicketReservationSignature {
  return (ticketReservationId: string) =>
    universalDeleteTicketReservation(
      ticketReservationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTicketReservationSignature {
  /**
   * Deletes a ticket reservation permanently.
   *
   * Completely removes the reservation and releases all reserved tickets back into the inventory to available inventory. This action can't be undone.
   *
   * Call [Cancel Ticket Reservation](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-reservations/cancel-ticket-reservation) if you want to change the reservation status while keeping the record.
   * @param - ID of the ticket reservation to delete.
   */
  (ticketReservationId: string): Promise<
    NonNullablePaths<
      DeleteTicketReservationResponse,
      | `ticketReservation.status`
      | `ticketReservation.tickets`
      | `ticketReservation.tickets.${number}.ticketInfo.pricingPlan.orderId`
      | `ticketReservation.tickets.${number}.ticketInfo.pricingPlan.planId`
      | `ticketReservation.tickets.${number}.price.value`
      | `ticketReservation.tickets.${number}.price.currency`
      | `ticketReservation.tags.privateTags.tagIds`,
      7
    >
  >;
}

export function bulkUpdateTicketReservationTags(
  httpClient: HttpClient
): BulkUpdateTicketReservationTagsSignature {
  return (
    ids: string[],
    options?: NonNullablePaths<
      BulkUpdateTicketReservationTagsOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdateTicketReservationTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateTicketReservationTagsSignature {
  /**
   * Updates tags on multiple ticket reservations.
   *
   * Allows adding or removing tags from multiple reservations in a single operation.
   * Specify reservation IDs and the tags to assign or unassign. If a tag appears
   * in both assign and unassign lists, it will be assigned.
   *
   * Use this for immediate tag updates on a known set of reservations.
   * @param - List of ticket reservation IDs to update.
   */
  (
    ids: string[],
    options?: NonNullablePaths<
      BulkUpdateTicketReservationTagsOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateTicketReservationTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateTicketReservationTagsByFilter(
  httpClient: HttpClient
): BulkUpdateTicketReservationTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateTicketReservationTagsByFilterOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdateTicketReservationTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateTicketReservationTagsByFilterSignature {
  /**
   * Updates tags on multiple ticket reservations asynchronously using filters.
   *
   * Allows bulk tag updates on reservations matching specified criteria.
   * The operation runs asynchronously and returns a job ID for tracking progress.
   * An empty filter will update all reservations.
   *
   * If a tag appears in both assign and unassign lists, it will be assigned.
   * Use this for large-scale tag operations based on reservation properties.
   * @param - Filter criteria to select reservations for tag updates.
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateTicketReservationTagsByFilterOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateTicketReservationTagsByFilterResponse,
      `jobId`,
      2
    >
  >;
}

export function cancelTicketReservation(
  httpClient: HttpClient
): CancelTicketReservationSignature {
  return (ticketReservationId: string) =>
    universalCancelTicketReservation(
      ticketReservationId,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelTicketReservationSignature {
  /**
   * Cancels an existing ticket reservation.
   *
   * Changes the reservation status to `CANCELED_MANUALLY` and releases the reserved
   * tickets back to available inventory. Once cancelled, a reservation cannot be
   * restored. Only `PENDING` and `CONFIRMED` reservations can be cancelled.
   * @param - ID of the ticket reservation to cancel.
   */
  (ticketReservationId: string): Promise<
    NonNullablePaths<
      CancelTicketReservationResponse,
      | `ticketReservation.status`
      | `ticketReservation.tickets`
      | `ticketReservation.tickets.${number}.ticketInfo.pricingPlan.orderId`
      | `ticketReservation.tickets.${number}.ticketInfo.pricingPlan.planId`
      | `ticketReservation.tickets.${number}.price.value`
      | `ticketReservation.tickets.${number}.price.currency`
      | `ticketReservation.tags.privateTags.tagIds`,
      7
    >
  >;
}

export const onTicketReservationCreated = EventDefinition(
  'wix.events.v1.ticket_reservation_created',
  true,
  (event: TicketReservationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.expirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TicketReservationCreatedEnvelope>();
export const onTicketReservationDeleted = EventDefinition(
  'wix.events.v1.ticket_reservation_deleted',
  true,
  (event: TicketReservationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.expirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TicketReservationDeletedEnvelope>();
export const onTicketReservationUpdated = EventDefinition(
  'wix.events.v1.ticket_reservation_updated',
  true,
  (event: TicketReservationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.expirationDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.expirationDate' },
          ],
        },
      ])
    )
)<TicketReservationUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddTicketsToCurrentTicketReservationRequest,
  AddTicketsToCurrentTicketReservationResponse,
  AddTicketsToTicketReservationRequest,
  AddTicketsToTicketReservationResponse,
  AdditionalReservationSettings,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateTicketReservationTagsByFilterOptions,
  BulkUpdateTicketReservationTagsByFilterRequest,
  BulkUpdateTicketReservationTagsByFilterResponse,
  BulkUpdateTicketReservationTagsOptions,
  BulkUpdateTicketReservationTagsRequest,
  BulkUpdateTicketReservationTagsResponse,
  BulkUpdateTicketReservationTagsResult,
  CancelTicketReservationOnGracePeriodEndRequest,
  CancelTicketReservationOnGracePeriodEndResponse,
  CancelTicketReservationRequest,
  CancelTicketReservationResponse,
  CatalogReference,
  CheckIn,
  CheckoutType,
  CommonMoney,
  ConfirmTicketReservationRequest,
  ConfirmTicketReservationResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  CreateTicketReservationRequest,
  CreateTicketReservationResponse,
  DeleteCurrentTicketReservationRequest,
  DeleteCurrentTicketReservationResponse,
  DeleteTicketReservationRequest,
  DeleteTicketReservationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Error,
  EventKey,
  EventMetadata,
  EventSettingsSnapshot,
  ExpireTicketReservationRequest,
  ExpireTicketReservationResponse,
  ExtendedFields,
  FeeType,
  FixArchivedTicketReservationRequest,
  FixArchivedTicketReservationResponse,
  FormResponse,
  FormattedAddress,
  GetCurrentTicketReservationRequest,
  GetCurrentTicketReservationResponse,
  GetTicketReservationRequest,
  GetTicketReservationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  ItemMetadata,
  MessageEnvelope,
  Money,
  OnlineConferencingLogin,
  OrderArchived,
  OrderStatus,
  OrderTicketsCanceled,
  OrderUnarchived,
  PricingPlan,
  PromotionErrors,
  PromotionType,
  QueryEventsWithPaidReservationsRequest,
  QueryEventsWithPaidReservationsResponse,
  RemoveTicketsFromCurrentTicketReservationRequest,
  RemoveTicketsFromCurrentTicketReservationResponse,
  RemoveTicketsFromTicketReservationRequest,
  RemoveTicketsFromTicketReservationResponse,
  ReservationStatus,
  RestoreInfo,
  SeatInfo,
  ServiceFee,
  StandardDetails,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  TagList,
  Tags,
  Ticket,
  TicketDefinitionBalance,
  TicketInfo,
  TicketLineItem,
  TicketReservation,
  TicketReservationBalance,
  TicketReservationBalanceUpdated,
  TicketReservationCreatedEnvelope,
  TicketReservationDeletedEnvelope,
  TicketReservationUpdatedEnvelope,
  Type,
  UpdateCurrentTicketReservationRequest,
  UpdateCurrentTicketReservationResponse,
  UpdateTicketReservationCancelableRequest,
  UpdateTicketReservationCancelableResponse,
  UpdateTicketReservationRequest,
  UpdateTicketReservationResponse,
  WebhookIdentityType,
} from './events-v1-ticket-reservation-ticket-reservations.universal.js';
